import React from "react"
import { useTranslation } from "react-i18next"

import { Obj, connect } from "scrivito"
import { BuildingReferenceObj } from "scrivito/Objs/BuildingReference/BuildingReferenceObjClass"
import { ProductImage } from "scrivito/Objs/Product/Components/ProductImage"
import {
  ProductAttributes,
  ProductObj
} from "scrivito/Objs/Product/ProductObjClass"
import { ProductBaseVariantAttributes } from "scrivito/Objs/ProductBaseVariant/ProductBaseVariantObjClass"
import { ScrivitoImage } from "scrivito/scrivito-image/ScrivitoImage"
import { ScrivitoLink } from "scrivito/scrivito-link/ScrivitoLink"
import { ensureString } from "shared/lib/ensureString"
import { Ref } from "shared/types/Ref"
import { BuildingType } from "shared/types/references/BuildingType"

import { useBuildingReferenceData } from "entities/building-references/model/useBuildingReferenceData"
import { BuildingReferenceCard } from "entities/building-references/ui/building-reference-card/BuildingReferenceCard"

export interface BuildingReferenceGridCardProps
  extends React.HTMLProps<HTMLAnchorElement> {
  buildingReferenceObj: BuildingReferenceObj
  priorityProducts?: string[] | null
  linkRef?: Ref<HTMLAnchorElement>
}

function getProductKind(
  obj: Obj<ProductAttributes | ProductBaseVariantAttributes>
) {
  switch (obj.objClass()) {
    case "ProductBaseVariant":
      return (obj.parent()?.get("kind") as string) ?? ""
    case "Product":
      return ((obj as ProductObj).get("kind") as string) ?? ""
    default:
      return ""
  }
}

function getProductType(
  obj: Obj<ProductAttributes | ProductBaseVariantAttributes>
) {
  switch (obj.objClass()) {
    case "ProductBaseVariant":
      return (obj.parent()?.get("productType") as string) ?? ""
    case "Product":
      return ((obj as ProductObj).get("productType") as string) ?? ""
    default:
      return ""
  }
}

function formatInfoText(buildingTypes?: string, city?: string) {
  if (buildingTypes && city) {
    return `${buildingTypes} | ${city}`
  }
  return buildingTypes || city
}

export function BuildingReferenceGridCardComponent({
  buildingReferenceObj,
  linkRef,
  priorityProducts,
  ...rest
}: BuildingReferenceGridCardProps) {
  const { t } = useTranslation()
  const { productObjs, image } = useBuildingReferenceData({
    id: buildingReferenceObj.id()
  })

  const sortedProductObjs = Array.isArray(priorityProducts)
    ? [...productObjs].sort(a =>
        priorityProducts.includes(ensureString(getProductType(a))) ? -1 : 1
      )
    : productObjs

  const products = sortedProductObjs?.map(productObj => ({
    title: productObj.get("titleShort"),
    kind: getProductKind(productObj),
    image: <ProductImage obj={productObj} />
  }))

  const buildingTypes: BuildingType[] | undefined = buildingReferenceObj.get(
    "buildingType"
  ) as BuildingType[] | undefined

  const formattedBuildingTypes = buildingTypes
    ?.map(buildingType => t(`BuildingReference.BuildingType.${buildingType}`))
    .join(", ")

  const city = buildingReferenceObj.get("contact")?.get("city") ?? ""

  const infoText = formatInfoText(
    formattedBuildingTypes,
    typeof city === "string" ? city : undefined
  )

  return (
    <ScrivitoLink
      to={buildingReferenceObj}
      className="building-reference-grid__anchor anchor"
      ref={linkRef}
      {...rest}
    >
      <BuildingReferenceCard.Root className="card--elevated">
        <BuildingReferenceCard.Title>
          {buildingReferenceObj.get("title")}
        </BuildingReferenceCard.Title>
        <BuildingReferenceCard.Image>
          <ScrivitoImage
            content={image || null}
            attribute="image"
            alt={ensureString(image?.get("alternativeText"))}
          />
        </BuildingReferenceCard.Image>
        <BuildingReferenceCard.InfoText>
          {infoText}
        </BuildingReferenceCard.InfoText>
        <BuildingReferenceCard.ProductList products={products} />
      </BuildingReferenceCard.Root>
    </ScrivitoLink>
  )
}

export const BuildingReferenceGridCard = React.memo(
  connect(BuildingReferenceGridCardComponent)
) as typeof BuildingReferenceGridCardComponent
